// The People card's data model: three sources that key the same person differently,
// joined on a normalised name/email.
//
//   fn_suggest_recipient  → [{ contact, email?, score, why, stale, days_quiet }]
//   mv_contact_engagement → [{ contact, contact_name, messages_in, requests_made,
//                              total_engagement, days_quiet, answers_in_hours }]
//
// `contact` is a name on the recipient rows and can be an email on engagement rows,
// where `contact_name` carries the name — so a person matches when ANY of their
// normalised keys (name or email) is shared.

import { fmtNumber } from '../../shared/format';

/** Lowercase, trim, collapse whitespace — so "  Carrie  BROWN " equals "carrie brown". */
export const norm = (v) => String(v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const recipientKeys = (r) => [norm(r?.contact), norm(r?.email)].filter(Boolean);
const engagementKeys = (c) => [norm(c?.contact_name), norm(c?.contact)].filter(Boolean);

/** Whether two key lists share at least one normalised key. */
export const sharesKey = (a, b) => a.some((k) => b.includes(k));

/** Best score first; a negative score is correct (silence) and simply sorts last. */
export const rankRecipients = (rows) =>
  [...(rows || [])].sort((a, b) => (Number(b?.score) || 0) - (Number(a?.score) || 0));

/** Reply speed as a finite number, or null (they write but never in reply — not an error, not zero). */
export const hoursOf = (row) => {
  const n = row?.answers_in_hours;
  return n === null || n === undefined || n === '' || !Number.isFinite(Number(n)) ? null : Number(n);
};

/**
 * A reply speed as shown: "<1h" under an hour (a rounded "~0h" reads as a bug), otherwise
 * "~Nh" rounded. Display only: comparisons (FASTEST) keep using the raw number from hoursOf.
 * @param {number} hours  A finite reply speed from hoursOf.
 * @returns {string}      e.g. "<1h", "~1h", "~102h"
 */
export const formatAnswerTime = (hours) => (hours < 1 ? '<1h' : `~${fmtNumber(hours)}h`);

/** The quickest reply speed among ALL engagement rows, or null when none has one. */
export const fastestHours = (engagement) => {
  const hours = (engagement || []).map(hoursOf).filter((h) => h !== null);
  return hours.length ? Math.min(...hours) : null;
};

/** The engagement row for a recipient, or null. */
export const engagementFor = (recipient, engagement) => {
  if (!recipient) return null;
  const keys = recipientKeys(recipient);
  return (engagement || []).find((c) => sharesKey(keys, engagementKeys(c))) || null;
};

/**
 * The Write-to person: the top recipient, with reply speed joined from engagement.
 *
 * `fastest` is true ONLY when this person's reply speed equals the minimum across
 * all engagement rows. The recommendation is chosen by score, not speed, so the
 * badge must never sit on someone who is not actually the quickest; a tie for the
 * minimum still counts (they are a fastest). No hours, no badge.
 *
 * @returns {{recipient:object, hours:number|null, fastest:boolean, stale:boolean}|null}
 */
export const buildWriteTo = (recipients, engagement) => {
  const recipient = rankRecipients(recipients)[0];
  if (!recipient) return null;
  const hours = hoursOf(engagementFor(recipient, engagement));
  const min = fastestHours(engagement);
  return { recipient, hours, fastest: hours !== null && min !== null && hours === min, stale: recipient.stale === true };
};

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+$/;

/**
 * The header's stand-in contact: the Write-to person (best-scored recipient), as
 * `{ name, email }`. `contact` is a name or an email; an email-shaped one fills Email
 * and is shown as the name too, otherwise the row's own `email` fills Email when it is email-shaped. A recipient
 * flagged stale ("may have left") is never offered, and neither is a row with no name
 * or email, so the header falls back to its placeholders instead.
 *
 * @param {object[]|null|undefined} recipients  fn_suggest_recipient rows
 * @returns {{name:string, email:string|null}|null}
 */
export const suggestedContact = (recipients) => {
  const top = rankRecipients(recipients)[0];
  if (!top || top.stale === true) return null;
  const contact = String(top.contact ?? '').trim();
  // An `email` that is not email-shaped ("n/a") is dropped, so the header never renders a bad mailto: link.
  const email = EMAIL_SHAPE.test(String(top.email ?? '').trim()) ? String(top.email).trim() : null;
  if (EMAIL_SHAPE.test(contact)) return { name: contact, email: contact };
  const name = contact || email;
  return name ? { name, email } : null;
};

/**
 * Everyone else worth knowing: engagement rows (most engaged first) minus the
 * Write-to person, then any recipient with no engagement row (best score first).
 * A recipient flagged stale greys its engagement row too — never hidden.
 *
 * @param {object[]} recipients
 * @param {object[]} engagement
 * @param {object|null} writeTo  The recipient chosen as Write-to (excluded here).
 * @returns {Array<{id:string,name:string,hours:number|null,quietDays:number|null,stale:boolean,why:string|null}>}
 */
export const buildWhoAnswers = (recipients, engagement, writeTo) => {
  const writeKeys = writeTo ? recipientKeys(writeTo) : [];
  const ranked = rankRecipients(recipients);
  const isWriteTo = (keys) => writeKeys.length > 0 && sharesKey(writeKeys, keys);

  const fromEngagement = [...(engagement || [])]
    .sort((a, b) => (Number(b?.total_engagement) || 0) - (Number(a?.total_engagement) || 0))
    .filter((c) => !isWriteTo(engagementKeys(c)))
    .map((c, i) => {
      const keys = engagementKeys(c);
      const match = ranked.find((r) => sharesKey(recipientKeys(r), keys));
      const quiet = Number(c?.days_quiet);
      return {
        id: c.contact || c.contact_name || `e${i}`,
        name: c.contact_name || c.contact || '—',
        hours: hoursOf(c),
        quietDays: c?.days_quiet != null && Number.isFinite(quiet) ? quiet : null,
        stale: match?.stale === true,
        why: null,
      };
    });

  const engagementKeyLists = (engagement || []).map(engagementKeys);
  const recipientsOnly = ranked
    .filter((r) => r !== writeTo)
    .filter((r) => !engagementKeyLists.some((keys) => sharesKey(recipientKeys(r), keys)))
    .map((r, i) => ({
      id: r.contact || r.email || `r${i}`,
      name: r.contact || r.email || '—',
      hours: null,
      quietDays: r?.days_quiet != null && Number.isFinite(Number(r.days_quiet)) ? Number(r.days_quiet) : null,
      stale: r.stale === true,
      why: r.why || null,
    }));

  return [...fromEngagement, ...recipientsOnly];
};

/**
 * How many distinct people the People card lists: the Write-to person, everyone under
 * Who answers (all pages, not just the visible one) and the suggested owner, counted
 * once each when the same name appears in more than one part.
 *
 * @param {{recipient:object}|null} writeTo   From buildWriteTo.
 * @param {Array<{name:string}>} whoAnswers   From buildWhoAnswers.
 * @param {{name?:string}|null} owner         The top owner suggestion.
 * @returns {number}
 */
export const countPeople = (writeTo, whoAnswers, owner) => {
  const names = [
    writeTo ? writeTo.recipient?.contact || writeTo.recipient?.email : null,
    ...(whoAnswers || []).map((p) => p?.name),
    owner?.name,
  ]
    .map(norm)
    .filter(Boolean);
  return new Set(names).size;
};
